
import { Suspense, ReactNode, lazy, LazyExoticComponent, ComponentType, memo } from "react";
import { motion } from "framer-motion";
import { slideInVariants, fadeInVariants } from "@/lib/animations";
import SectionPlaceholder from "./SectionPlaceholder";
import { useAnimationSimplifier } from "@/hooks/use-animation-simplifier";
import { useIsMobile } from "@/hooks/use-mobile";

interface LazyLoadedSectionProps {
  Component: LazyExoticComponent<ComponentType<any>>;
  placeholderBg: string;
  placeholderHeight: string;
  animation?: "fade" | "slide";
  style?: React.CSSProperties;
}

const LazyLoadedSection = memo(({ 
  Component, 
  placeholderBg, 
  placeholderHeight,
  animation = "slide",
  style 
}: LazyLoadedSectionProps) => {
  const { shouldSimplifyAnimations } = useAnimationSimplifier();
  const isMobile = useIsMobile();
  
  const variants = animation === "fade" ? fadeInVariants : slideInVariants.up;
  
  // Simplify animations on mobile or when reduced motion is preferred
  const animationProps = shouldSimplifyAnimations || isMobile ? {
    initial: { opacity: 0 },
    whileInView: { opacity: 1 },
    viewport: { once: true, margin: "50px" },
    transition: { duration: 0.3 }
  } : {
    initial: "initial",
    whileInView: "animate",
    viewport: { once: true, amount: 0.1, margin: "100px" },
    variants
  };
  
  return (
    <Suspense fallback={<SectionPlaceholder bg={placeholderBg} height={placeholderHeight} />}>
      <motion.div
        {...animationProps}
        style={style}
      >
        <Component />
      </motion.div>
    </Suspense>
  );
});

LazyLoadedSection.displayName = 'LazyLoadedSection';

export default LazyLoadedSection;
